import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './CheckoutPage.css';

const STRIPE_LINKS = {
  monthly:   'https://buy.stripe.com/4gM4gz0M8c85cxK6LE1kA0A',
  quarterly: 'https://buy.stripe.com/cNi4gzamI3Bz1T60ng1kA0B',
  yearly:    'https://buy.stripe.com/7sYaEXbqM1tr0P20ng1kA0C',
};

const PLAN_IDS = ['monthly', 'quarterly', 'yearly'];
const PLAN_PRICES = { monthly: 12.89, quarterly: 30.57, yearly: 114.47 };
const PLAN_PERIODS = { monthly: '/mo', quarterly: '/qtr', yearly: '/yr' };

function IconLock() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
}
function IconCheck() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IconBack() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>;
}
function IconShield() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const planId = searchParams.get('plan') || 'monthly';

  const [selectedPlan, setSelectedPlan] = useState(planId);

  const [form, setForm] = useState({
    email: user?.email || '',
  });

  const featureKeys = ['priorityInbox', 'neighbourhoodIntel', 'smartAlerts', 'priceDropAlerts'];
  const features = featureKeys.map(k => t(`checkout.features.${k}`));

  const plans = PLAN_IDS.map(id => ({
    id,
    label: t(`checkout.plans.${id}.label`),
    price: PLAN_PRICES[id],
    period: PLAN_PERIODS[id],
    note: t(`checkout.plans.${id}.note`),
    badge: id === 'quarterly' ? t('checkout.mostPopular') : null,
    discount: id === 'monthly' ? null : t(`checkout.plans.${id}.discount`),
  }));

  const plan = plans.find(p => p.id === selectedPlan) || plans[0];
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePay = () => {
    const url = new URL(STRIPE_LINKS[selectedPlan]);
    if (form.email) url.searchParams.set('prefilled_email', form.email);
    if (user?.id) url.searchParams.set('client_reference_id', user.id);
    window.location.href = url.toString();
  };

  return (
    <div className="checkout-page">
      <header className="checkout-hero">
        <div className="checkout-hero__inner">
          <button className="checkout-back checkout-hero__back" onClick={() => navigate(-1)}><IconBack /> {t('checkout.back')}</button>
          <span className="checkout-hero__eyebrow">{t('checkoutHero.eyebrow')}</span>
          <h1 className="checkout-hero__title">{t('checkoutHero.title')}</h1>
          <p className="checkout-hero__sub">{t('checkoutHero.sub')}</p>
        </div>
      </header>

      <div className="checkout-page__inner">

        <div className="checkout-page__main">
          <button className="checkout-back checkout-back--mobile" onClick={() => navigate(-1)}><IconBack /> {t('checkout.back')}</button>

          <div className="checkout-plan-step">
            <h1 className="checkout-title">{t('checkout.choosePlan')}</h1>
            <p className="checkout-sub">{t('checkout.unlockPro')}</p>

            <div className="checkout-plans">
              {plans.map(p => (
                <div key={p.id}
                  className={`checkout-plan ${selectedPlan === p.id ? 'checkout-plan--active' : ''}`}
                  onClick={() => setSelectedPlan(p.id)}>
                  {p.badge && <div className="checkout-plan__badge">{p.badge}</div>}
                  <div className="checkout-plan__top">
                    <div className="checkout-plan__radio">
                      {selectedPlan === p.id && <div className="checkout-plan__radio-dot" />}
                    </div>
                    <div className="checkout-plan__label">{p.label}</div>
                  </div>
                  <div className="checkout-plan__price">
                    <span className="checkout-plan__eur">€</span>
                    <span className="checkout-plan__amount">{p.price.toFixed(2)}</span>
                    <span className="checkout-plan__period">{p.period}</span>
                  </div>
                  <div className="checkout-plan__note">{p.note}</div>
                  {p.discount && <div className="checkout-plan__discount">{p.discount}</div>}
                </div>
              ))}
            </div>

            <div className="checkout-field" style={{marginTop: 24}}>
              <label>{t('checkout.emailAddress')}</label>
              <input type="email" value={form.email}
                onChange={e => setField('email', e.target.value)}
                placeholder="you@example.com" />
            </div>

            <div className="checkout-features">
              <div className="checkout-features__title">{t('checkout.everythingIncluded')}</div>
              <div className="checkout-features__grid">
                {features.map(f => (
                  <div key={f} className="checkout-feature">
                    <div className="checkout-feature__check"><IconCheck /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>

            <button className="checkout-cta" onClick={handlePay}>
              {t('checkout.paySecure').replace('{amount}', plan.price.toFixed(2))}
            </button>

            <div className="checkout-secure" style={{marginTop: 12}}>
              <IconShield /> {t('checkout.ssl')} &nbsp;·&nbsp; <IconLock /> {t('checkout.pci')} &nbsp;·&nbsp; {t('checkout.poweredByStripe')}
            </div>

            <p className="checkout-terms">
              {t('checkout.terms').split(t('checkout.termsLink'))[0]}
              <a href="#">{t('checkout.termsLink')}</a>
              {t('checkout.terms').split(t('checkout.termsLink'))[1]}
            </p>
          </div>
        </div>

        <div className="checkout-page__summary">
          <div className="checkout-summary">
            <div className="checkout-summary__header">{t('checkout.orderSummary')}</div>
            <div className="checkout-summary__plan">
              <div className="checkout-summary__plan-name">{t('checkout.productName')}</div>
              <div className="checkout-summary__plan-label">{plan.label} {t('checkout.planSuffix')}</div>
            </div>
            <div className="checkout-summary__line"><span>{t('checkout.priceVat')}</span><span>€{plan.price.toFixed(2)}</span></div>
            <div className="checkout-summary__total"><span>{t('checkout.total')}</span><span>€{plan.price.toFixed(2)}</span></div>
            {plan.discount && <div className="checkout-summary__saving">{t('checkout.youSave').replace('{discount}', plan.discount)}</div>}
            <div className="checkout-summary__features">
              {features.slice(0, 5).map(f => (
                <div key={f} className="checkout-summary__feature"><IconCheck /> {f}</div>
              ))}
              {features.length > 5 && (
                <div className="checkout-summary__feature checkout-summary__feature--more">
                  {t('checkout.moreFeatures').replace('{n}', features.length - 5)}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
